import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useForm } from 'react-hook-form';
import { useAppDispatch } from 'store/store';
import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useNavigationConfirmation } from 'hooks/useNavigationConfirmation';
import { closeExitDialog, resetSignUpState, setSignUpState } from '../signUpSlice';
import {
  SIGNUP_SKIP_BROWSER_CONFIRMATION_STEPS,
  SIGNUP_STEP_PROGRESS_PRECENTAGE,
} from '../constants';
import { SignupFormData } from '../types';
import { useSignupData } from './useSignupData';
import { useSignupError } from './useSignupError/useSignupError';
import { SignupError } from './useSignupError/SignupError';
import { usePolicyAcceptanceStep } from './usePolicyAcceptanceStep/usePolicyAcceptanceStep';
import { useWelcomeStep } from './useWelcomeStep';
import { useSSOSignup } from './useSSOSignup';
import { useSignupSectionNavigation } from './useSignupSectionNaivgation';
import { useAccessCodeStep } from './useAccessCodeStep/useAccessCodeStep';
import { useVerifyEligibilityStep } from './useVerifyEligibilityStep/useVerifyEligibilityStep';
import { useAccessCodeMatchedStep } from './useAccessCodeMatchedStep/useAccessCodeMatchedStep';
import { useUserTypeSelectionStep } from './useUserTypeSelectionStep/useUserTypeSelectionStep';
import { useShortcutSSOStep } from './useShortcutSSOStep/useShortcutSSOStep';

export const useSignup = () => {
  const [searchParams] = useSearchParams();
  const codeFromQuery = searchParams.get('accessCode') ?? '';
  const formMethods = useForm<SignupFormData>({
    mode: 'onBlur',
    defaultValues: {
      accessCode: codeFromQuery,
    },
  });
  const { getValues: getFormValues } = formMethods;
  const signUpFormData = getFormValues();
  const { currentStep, showExitDialog, userType, eligibilityCheckMethod, ssoAuthConfig } =
    useSignupData();
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressPercentage = SIGNUP_STEP_PROGRESS_PRECENTAGE[currentStep];
  const appDispatch = useAppDispatch();
  const handleExitDialogClose = () => appDispatch(closeExitDialog());
  const { getValues, setError, reset: resetSignupForm } = formMethods;
  const { translation } = useTranslationContext();
  const { submit: submitAccessCodeStep, isSubmitting: isSubmittingAccessCodeStep } =
    useAccessCodeStep();
  const { submit: submitAccessCodeMatchedStep } = useAccessCodeMatchedStep();
  const { submit: submitUserTypeSelectionStep } = useUserTypeSelectionStep();
  const { isSubmitting: isSubmittingVerifyEligibilityStep, submit: submitVerifyEligibilityStep } =
    useVerifyEligibilityStep(formMethods);
  const { isSubmitting: isSubmittingPolicyAcceptanceStep, submit: submitPolicyAcceptanceStep } =
    usePolicyAcceptanceStep();
  const { submit: submitShortcutSSOStep } = useShortcutSSOStep();
  const { submit: submitWelcomeStep } = useWelcomeStep();
  const { navigateToNextStep, navigateToPreviousStep } = useSignupSectionNavigation();
  const openHelpDialog = () => setIsHelpDialogOpen(true);
  const closeHelpDialog = () => setIsHelpDialogOpen(false);
  const { isShowingAlertError, alertError, isShowingDialogError, dialogError, showError } =
    useSignupError(openHelpDialog);
  const { hasAuthCode, handleOIDCSignIn } = useSSOSignup(formMethods, showError);

  const enabledNavigationConfirmation =
    !SIGNUP_SKIP_BROWSER_CONFIRMATION_STEPS.includes(currentStep);
  // This hook is used to manage the beforeunload event listener for navigation confirmation
  // it shows a browser confirmation prompt when the user tries to navigate away from the sign-up flow
  const { suppressBeforeUnload } = useNavigationConfirmation(enabledNavigationConfirmation);

  useEffect(() => {
    // Scroll to the top when changing steps
    containerRef.current?.scrollTo(0, 0);
    // Move focus to the header of the new step
    const header = containerRef.current?.querySelector('h1, h2, h3, h4, h5, h6');
    if (header) {
      const headerElement = header as HTMLElement;
      headerElement.tabIndex = -1; // Make sure it's focusable
      headerElement.style.outline = 'none'; // Remove the extra focus outline for non-screenreaders
      headerElement.focus();
    }
  }, [currentStep]);

  // this is used to determine if the back button should be disabled in the sign up flow
  const isBackButtonDisabled =
    currentStep === 'SSOSignupLoading' ||
    currentStep === 'SSOSignUpSuccess' ||
    currentStep === 'Welcome';

  const isApiRequestInProgress =
    isSubmittingAccessCodeStep ||
    isSubmittingVerifyEligibilityStep ||
    isSubmittingPolicyAcceptanceStep;

  const handleConfirmExit = () => {
    resetSignupForm();
    appDispatch(resetSignUpState());
    appDispatch(closeExitDialog());
  };

  // Form Submission
  const handleAccessCodeSubmit = async (accessCode: string) => {
    try {
      await submitAccessCodeStep(accessCode);
      navigateToNextStep('AccessCodeMatched');
    } catch (error) {
      const signupError = error as SignupError;

      if (signupError.type === 'InvalidAccessCode') {
        setError('accessCode', {
          message: translation('access_code_field_error'),
        });
      }

      showError(signupError.type);
    }
  };

  const handleAccessCodeMatchedSubmit = () => {
    submitAccessCodeMatchedStep();
  };

  const handleUserTypeSelectionSubmit = () => {
    submitUserTypeSelectionStep(getValues('selectedUserType'));
  };

  const handleConditionalEligibilityCheckSubmit = () => {
    navigateToNextStep('DobEligibilityCheck');
    if (dialogError.type === 'IneligibleUser' || dialogError.type === 'UnderageUser') {
      setError('memberDateOfBirth', {
        message: translation('eligibility_field_error'),
      });
    }
  };

  const handleShortCutSSOSubmit = async () => {
    try {
      // Suppress the browser's confirmation dialog that appears when navigating away to the identity provider
      suppressBeforeUnload();
      await submitShortcutSSOStep(getValues('accessCode'), handleOIDCSignIn);
    } catch (error) {
      console.error('Error during SSO redirect:', error);
      // Handle error case
    }
  };

  const handleSSOSuccessSubmit = async () => {
    navigateToNextStep('MemberProfileCreation');
  };

  const handleDobEligibilityCheckSubmit = async () => {
    try {
      await submitVerifyEligibilityStep();
      navigateToNextStep('EligibilityVerified');
    } catch (error) {
      const signupError = error as SignupError;

      if (signupError.type !== 'NoNetwork') {
        setError(eligibilityCheckMethod === 'memberId' ? 'memberId' : 'memberLastName', {
          message: translation('eligibility_field_error'),
        });
        navigateToPreviousStep();
      }

      showError(signupError.type);
    }
  };

  const handleEligibilityVerifiedSubmit = () => {
    navigateToNextStep(
      userType === 'caregiver' ? 'CaregiverProfileCreation' : 'MemberProfileCreation',
    );
  };

  const handleCaregiverProfileCreationSubmit = () => {
    navigateToNextStep('MemberProfileCreation');
  };

  const handleMemmberProfileCreationSubmit = () => {
    // SSO users are not required to set a password
    navigateToNextStep(ssoAuthConfig ? 'PolicyAcceptance' : 'AccountCredentials');
  };

  const handleAccountCredentialsSubmit = () => {
    navigateToNextStep('PolicyAcceptance');
  };

  const handlePolicyAcceptanceSubmit = async () => {
    try {
      await submitPolicyAcceptanceStep(getValues());
      navigateToNextStep('Welcome');
    } catch (error) {
      const signupError = error as SignupError;

      switch (signupError.type) {
        case 'EmailAlreadyRegistered':
          setError('memberEmail', {
            message: translation('email_in_use_error_header'),
          });
          navigateToPreviousStep();
          break;
        case 'NoNetwork':
          break;
        case 'UserEnrollmentFailed':
        default:
          navigateToPreviousStep();
      }

      showError(signupError.type);
    }
  };

  const handleWelcomeSubmit = () => {
    try {
      submitWelcomeStep();
    } catch (error) {
      const signupError = error as SignupError;
      showError(signupError.type);
    }
  };

  const handleFormSubmit = async (formData: SignupFormData) => {
    appDispatch(setSignUpState({ formData }));

    switch (currentStep) {
      case 'AccessCodeMatched':
        handleAccessCodeMatchedSubmit();
        break;
      case 'AccessCodeSubmission':
        await handleAccessCodeSubmit(formData.accessCode);
        break;
      case 'UserTypeSelection':
        handleUserTypeSelectionSubmit();
        break;
      case 'ConditionalEligibilityCheck':
        handleConditionalEligibilityCheckSubmit();
        break;
      case 'DobEligibilityCheck':
        await handleDobEligibilityCheckSubmit();
        break;
      case 'ShortCutSSO':
        await handleShortCutSSOSubmit();
        break;
      case 'SSOSignUpSuccess':
        await handleSSOSuccessSubmit();
        break;
      case 'EligibilityVerified':
        handleEligibilityVerifiedSubmit();
        break;
      case 'CaregiverProfileCreation':
        handleCaregiverProfileCreationSubmit();
        break;
      case 'MemberProfileCreation':
        handleMemmberProfileCreationSubmit();
        break;
      case 'AccountCredentials':
        handleAccountCredentialsSubmit();
        break;
      case 'PolicyAcceptance':
        await handlePolicyAcceptanceSubmit();
        break;
      case 'Welcome':
        handleWelcomeSubmit();
        break;
      default:
        break;
    }
  };

  return {
    formMethods,
    currentStep,
    progressPercentage,
    userType,
    eligibilityCheckMethod,
    isApiRequestInProgress,
    handleFormSubmit,
    showExitDialog,
    handleExitDialogClose,
    handleConfirmExit,
    hasAuthCode,
    isBackButtonDisabled,
    isHelpDialogOpen,
    openHelpDialog,
    closeHelpDialog,
    signUpFormData,
    isShowingAlertError,
    alertError,
    isShowingDialogError,
    dialogError,
    containerRef,
  };
};
